function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState } from 'react';
import cn from 'classnames';
import { isAfter, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { InvoicePaymentFailureCode } from 'anchor-scout-common/types';
import { Button, Callout, Typography } from 'seagull';
import { useOverlayContext, useStoreActions, useStore, useIoContext } from "../../contexts";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { invoiceModel, relationshipModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { helpArticles } from "../../utils/helpArticles";
import ChangesApprovalInfoBox from "../ChangesApprovalInfoBox/ChangesApprovalInfoBox";
import ExternalLinkButton from "../ExternalLinkButton";
import styles from "./FullPageInvoice.module.scss";
import { makeResumePaymentInvoiceAction, terminatedTooltip } from "./FullPageInvoice.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function FullPageInvoiceCallout(props) {
  const {
    id,
    text,
    breakOnNewLines,
    intent,
    actions,
    footer
  } = props;
  return /*#__PURE__*/_jsx(Callout, {
    intent: intent,
    testId: "fpi-".concat(id),
    footer: footer,
    withAction: !!(actions !== null && actions !== void 0 && actions.length) && /*#__PURE__*/_jsx(_Fragment, {
      children: actions.map(a => /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Button, {
          intent: a.intent,
          skin: a.skin,
          disabled: a.disabled,
          tooltip: a.tooltipTitle ? {
            title: /*#__PURE__*/_jsx("div", {
              "data-testid": "fpi-callout-tooltip",
              children: a.tooltipTitle
            }),
            placement: 'top'
          } : undefined,
          loading: a.loading,
          testId: "fpi-".concat(a.id),
          onClick: a.onClick,
          children: a.text
        }), a.errorMsg && /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "error",
          testId: "fpi-".concat(a.id, "-error"),
          children: a.errorMsg
        })]
      }, id))
    }),
    children: /*#__PURE__*/_jsx("div", {
      className: cn(breakOnNewLines && styles.breakOnNewLines),
      children: text
    })
  });
}
function showLearnAboutHandlingChargebacks(invoice, isVendor) {
  const failureCode = invoiceModel.paymentFailureCode(invoice);
  return isVendor && failureCode && [InvoicePaymentFailureCode.DisputeProductUnacceptable, InvoicePaymentFailureCode.DisputeCreditNotProcessed].includes(failureCode);
}
export function getFullPageInvoiceCallouts(props) {
  const {
    invoice,
    relationship,
    isVendor,
    disabling,
    now,
    onPayNow,
    onRetryCharge,
    onResumePayment,
    onApprovePayment,
    onDisableAutoApprove
  } = props;
  const hasPaymentMethod = relationshipModel.paymentMethod(relationship) != null;
  const customerName = relationshipModel.customerName(relationship);
  const sellerName = relationshipModel.sellerFullName(relationship);
  const isTerminated = relationshipModel.isTerminated(relationship);
  const autoApprovalDate = invoiceModel.autoApprovalDate(invoice);
  const paymentProcessingDate = invoiceModel.paymentProcessingDate(invoice);
  const disputedMessage = invoiceModel.disputedMessage(invoice);
  return compact([invoiceModel.isPaymentFailed(invoice) && {
    id: 'payment-failed',
    text: /*#__PURE__*/_jsxs(Typography, {
      testId: "payment-failed-text",
      children: [/*#__PURE__*/_jsx(Typography, {
        as: "span",
        weight: "normal",
        children: "Payment failed:"
      }), ' ', invoiceModel.paymentFailureMessage(invoice) || '', ' ', showLearnAboutHandlingChargebacks(invoice, isVendor) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ExternalLinkButton, {
          testId: "payment-failed-disputed-learn-more",
          openInNewTab: true,
          href: helpArticles.chargebacksAndDisputes('missing-auth'),
          children: "Learn more"
        }), ' ', "about handling chargebacks."]
      })]
    }),
    intent: 'warning',
    actions: [{
      id: 'retry-charge',
      skin: 'primaryOutline',
      text: 'Retry charge',
      intent: Intent.InvoiceRetryCharge,
      disabled: isTerminated || !invoice.isRetryable,
      tooltipTitle: isTerminated ? terminatedTooltip : !invoice.isRetryable ? 'In order to retry charge, please contact support@sayanchor.com' : undefined,
      onClick: onRetryCharge
    }]
  }, invoiceModel.isRequiresPaymentApproval(invoice) && {
    id: 'requires-approval',
    text: isVendor ? "This invoice requires your client's approval" : 'This invoice requires your approval',
    actions: compact([!isVendor && {
      id: 'approve-payment',
      text: 'Approve payment',
      intent: Intent.InvoiceApprovePayment,
      disabled: isTerminated,
      tooltipTitle: isTerminated ? terminatedTooltip : undefined,
      onClick: onApprovePayment
    }]),
    footer: autoApprovalDate && /*#__PURE__*/_jsx(ChangesApprovalInfoBox, {
      testId: "fpi-auto-approval",
      onDisable: async () => onDisableAutoApprove(invoice),
      disabling: disabling,
      autoApprovalDate: autoApprovalDate,
      showDisableButton: !isVendor
    })
  }, disputedMessage != null && {
    id: 'payment-disputed',
    intent: 'warning',
    text: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Typography, {
        as: "span",
        weight: "normal",
        children: "Payment disputed:"
      }), ' ', disputedMessage, ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
        testId: "payment-disputed-learn-more",
        openInNewTab: true,
        href: helpArticles.chargebacksAndDisputes(),
        children: "Learn more"
      }), ' ', "about handling chargebacks."]
    })
  }, invoiceModel.isPending(invoice) && invoice.netTerms > 0 && {
    id: 'future-payment',
    text: "The payment will be processed automatically on ".concat(paymentProcessingDate),
    actions: compact([!isVendor && hasPaymentMethod && isAfter(startOfDay(new Date(paymentProcessingDate)), startOfDay(now)) && {
      id: 'pay-now',
      text: 'Pay now',
      intent: Intent.InvoicePayNow,
      disabled: isTerminated,
      tooltipTitle: isTerminated ? terminatedTooltip : undefined,
      onClick: onPayNow
    }])
  }, invoiceModel.isPaymentStopped(invoice) && {
    id: 'payment-stopped',
    text: isVendor ? "".concat(customerName, " has stopped the payment for this invoice with the following note: \"").concat(invoiceModel.paymentStopReason(invoice), "\".\nPlease contact ").concat(customerName, " to resume the payment.") : 'The payment for this invoice was stopped. To resume that payment click "Resume Payment".',
    breakOnNewLines: true,
    intent: isVendor ? 'warning' : 'info',
    actions: compact([!isVendor && _objectSpread(_objectSpread({}, makeResumePaymentInvoiceAction(isTerminated)), {}, {
      id: 'resume-payment',
      onClick: onResumePayment
    })])
  }, invoiceModel.isRefundRequested(invoice.paymentDisplayStatus) && {
    id: 'refund-requested',
    text: 'A request for a refund on this payment has been made and will be initiated once the payment has been successfully processed.',
    intent: 'warning'
  }, invoiceModel.isRefundFailed(invoice.paymentDisplayStatus) && {
    id: 'refund-failed',
    text: 'Refund was unsuccessful. For assistance, please contact support@sayanchor.com.',
    intent: 'warning'
  }, invoiceModel.isPartiallyRefunded(invoice) && {
    id: 'partially-refunded',
    text: "This payment was partially refunded for $".concat(invoiceModel.totalRefunded(invoice), " ").concat(invoiceModel.hasRefundFees(invoice) ? '(including processing fees reimbursement)' : '', " by ").concat(sellerName),
    intent: 'info'
  }]);
}
export function ResolvePaymentCallout(props) {
  const {
    invoice,
    isVendor,
    reloadInvoice
  } = props;
  const {
    stripeUrl,
    clientSecret
  } = invoiceModel.processingAction(invoice);
  const {
    logBI
  } = useStoreActions();
  const {
    openNewTab,
    reactStripe: {
      useStripe
    }
  } = useIoContext();
  const stripe = useStripe();
  const overlayContext = useOverlayContext();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const impersonatingAs = useStore(selectors.impersonatingAs);
  async function onResolvePayment(_ref) {
    let {
      stripeUrl,
      clientSecret
    } = _ref;
    logBI(10590, {
      invoiceId: invoice.id
    });
    if (stripeUrl != null) {
      openNewTab(stripeUrl);
      document.addEventListener('visibilitychange', listener);
    } else if (stripe != null && clientSecret != null) {
      setLoading(true);
      setErrorMsg('');
      const {
        error
      } = await stripe.confirmCardPayment(clientSecret);
      await handlePromise(reloadInvoice());
      if (error) {
        setErrorMsg(error.message || 'An error occurred');
      }
      setLoading(false);
    }
    async function listener() {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', listener);
        overlayContext.startLoader();
        await handlePromise(reloadInvoice());
        overlayContext.stopLoader();
      }
    }
  }
  return /*#__PURE__*/_jsx(FullPageInvoiceCallout, {
    id: "payment-verification",
    text: /*#__PURE__*/_jsxs("div", {
      children: ["The credit card issuer requires ", isVendor ? 'your client' : 'you', " to perform multi-factor authentication in order to process this payment", errorMsg && /*#__PURE__*/_jsx(Typography, {
        testId: "fpi-resolve-payment-error",
        skin: "error",
        children: errorMsg
      })]
    }),
    actions: compact([(stripeUrl != null || clientSecret != null) && {
      id: 'resolve-payment',
      text: 'Resolve payment',
      intent: Intent.InvoiceResolvePayment,
      loading,
      tooltipTitle: isVendor ? 'Only your client can complete this action' : impersonatingAs ? 'This button is disabled while impersonating' : undefined,
      onClick: () => onResolvePayment({
        stripeUrl,
        clientSecret
      }),
      disabled: isVendor || !!impersonatingAs
    }])
  });
}